import { QuestionModel } from '@/models/content/question';
import { taxonomySerializer } from './taxonomy';
import { sessionService } from '@/providers/services/auth/session';
import { DEFAULT_IMAGES_PATH } from '@/utils/constants';
import { getQuestionTypeByApiType, gutCourseNames, truncateString } from '@/utils/utils';
import moment from 'moment';

export class QuestionSerializer {
  private static INSTANCE = new QuestionSerializer();

  static get instance() {
    return this.INSTANCE;
  }

  /**
   * Normalized data of resource by id
   * @return {Object}
   */
  public normalizeQuestionContent(response: any) {
    const taxonomy = taxonomySerializer.normalizeTaxonomyObject(response.taxonomy);
    response.taxonomy = taxonomy[0];
    return response;
  }

  public serializeQuestion(question: any): QuestionModel {
    const cdnUrls = sessionService.getCdnUrl();
    const contentCdnUrl = cdnUrls.content_cdn_url;
    const thumbnailUrl = question.thumbnail ?
      contentCdnUrl + question.thumbnail :
      `${window.location.origin}/` + DEFAULT_IMAGES_PATH.unit;
    const result: QuestionModel = {
      id: question.id,
      title: question.title,
      description: question.description,
      subformat: question.contentSubFormat,
      format: question.format || 'question',
      efficacy: question.efficacy,
      relevance: question.relevance,
      engagement: question.engagement,
      standards: taxonomySerializer.normalizeTaxonomyObject(question.taxonomy),
      creator: taxonomySerializer.normalizeCreator(question.creator),
      isVisibleOnProfile: false,
      thumbnailUrl,
    };
    return result;
  }

  public normalizeReadQuestion(questionData: any, index: number = 0) {
    const cdnUrls = sessionService.getCdnUrl();
    const basePath = cdnUrls.content_cdn_url;
    const format = getQuestionTypeByApiType(
      questionData.content_subformat,
    );
    const standards = questionData.taxonomy || {};
    const metadata = questionData.metadata || {};
    const subQuestions = questionData.subQuestions || null;
    const question: any = {
      id: questionData.id,
      title: questionData.title,
      narration: questionData.narration,
      answerDetails: questionData.answerDetails,
      hint: questionData.hints,
      type: format,
      content_format: questionData.content_format,
      thumbnail: questionData.thumbnail
        ? basePath + questionData.thumbnail
        : null,
      text: questionData.description,
      publishStatus: questionData.publish_status,
      owner: questionData.creator_id,
      creator: questionData.original_creator_id,
      standards: taxonomySerializer.normalizeTaxonomyObject(questionData.taxonomy),
      hints: null, // TODO
      explanation: null, // TODO
      isVisibleOnProfile:
        typeof questionData.visible_on_profile !== 'undefined'
          ? questionData.visible_on_profile
          : true,
      order: questionData.sequence_id || index + 1,
      metadata,
      audience:
        metadata.audience && metadata.audience.length > 0
          ? metadata.audience
          : [],
      depthOfknowledge:
        metadata.depth_of_knowledge && metadata.depth_of_knowledge.length > 0
          ? metadata.depth_of_knowledge
          : [],
      courseId: questionData.course_id,
      unitId: questionData.unit_id,
      lessonId: questionData.lesson_id,
      collectionId: questionData.collection_id,
      maxScore: questionData.max_score || 1,
      hintExplanationDetail: questionData.hint_explanation_detail
        ? questionData.hint_explanation_detail.exemplar
        : null,
      exemplarDocs: questionData.hint_explanation_detail
        ? questionData.hint_explanation_detail.exemplar_docs
        : [],
    };

    if (subQuestions) {
      question.subQuestions = subQuestions.map((item: any, subindex: number) =>
        this.normalizeReadQuestion(item, subindex));
    }
    let answers = this.normalizeAnswerArray(questionData.answer, format);
    if (question.type === 'HS_IMG') {
      answers = answers.map((answer: any) => {
        // adding the basepath for HS Image
        answer.text = basePath + answer.text;
        return answer;
      });

    }
    if (
      ['SERP_IB', 'SERP_VT', 'SERP_CS', 'SERP_SD'].indexOf(question.type) !== -1
    ) {
      answers = answers.map((answer: any) => {
        answer.correctAnswer = answer.correctAnswer.map((item: any) =>
          typeof (item) === 'string' ? JSON.parse(item) : item,
        );
      });
    }
    question.answers = answers;
    if (
      question.type === 'SE_FIB' ||
      question.type === 'SE_FRQ'
    ) {
      question.answerDetails = questionData.answer;
      question.hints =
        questionData.hint_explanation_detail &&
          !questionData.hint_explanation_detail.exemplar &&
          !questionData.hint_explanation_detail.exemplar_docs
          ? questionData.hint_explanation_detail
          : null;
    }
    return question;
  }

  /**
   * Normalize an array of answers
   *
   * @param answerArray array of answers coming from the endpoint
   * @returns {AnswerModel[]}
   */
  public normalizeAnswerArray(answerArray: any, format: any) {
    const serializer = this;
    let result: any = [];
    if (answerArray && Array.isArray(answerArray)) {
      result = answerArray.map((answerData) => {
        return serializer.normalizeAnswer(answerData, format);
      });
    }
    return result;
  }

  /**
   * Normalize a single answer into a Answer object
   *
   * @param answerData Answer data
   * @returns {Answer}
   */
  public normalizeAnswer(answerData: any, format: string) {
    const id =
      format !== 'MA'
        ? window.btoa(encodeURIComponent(answerData.answer_text))
        : `answer_${answerData.sequence}`;
    return {
      id,
      sequence: answerData.sequence,
      isCorrect: answerData.is_correct === 1 || answerData.is_correct === true,
      text: answerData.answer_text,
      type: answerData.answer_type,
      highlightType: answerData.highlight_type,
      audioUrl: answerData.answer_audio_filename,
      correctAnswer: answerData.correct_answer || [],
      baseWords: answerData.base_words || [],
    };
  }

  public questionPulloutSerializer(collection: any, selectedQuestion: any, metadataLevels: any) {

    const defaultVectorValue = 0.5;
    let resultSet = {};
    if (collection) {
      const advertisementLevel = metadataLevels.advertisement_level || {};
      const audience = metadataLevels.audience || {};
      const grade = metadataLevels.grade || {};
      const license = metadataLevels.license || {};
      const educationalUse = metadataLevels.educational_use || {};
      const gradeLevelText: any = [];
      const collectionGrade = collection.metadata
        ? collection.metadata.grade
        : null;
      if (collectionGrade) {
        collectionGrade.forEach((gradeLevel: any) => {
          gradeLevelText.push(grade[gradeLevel]);
        });
      }

      resultSet = {
        descriptive: {
          title: collection.title,
          description: truncateString(collection.description),
        },
        creation: {
          'Published By': 'Gooru org',
          'Published Status': 'Published',
          'License': collection.license
            ? license[collection.license]
            : 'Public Domain',
          'created by': collection.owner.username,
          'created on': collection.publish_date
            ? moment(collection.publish_date).format('MMMM DD, YYYY')
            : null,
          'modified on': collection.modeified_date
            ? collection.modeified_date
            : null,
          'modified by': collection.modified_by,
        },

        educational: {
          'language': collection.info
            ? collection.info.language === 'eng'
              ? 'English'
              : collection.info.language
            : 'English',
          'educational use': collection.metadata
            ? educationalUse[collection.metadata.educational_use]
            : null,
          'accessbility': collection.accessibility,
          'grade': collection.metadata
            ? collection.metadata.grade
              ? gradeLevelText.join(', ')
              : null
            : null,
          'age-range': collection.age ? collection.age : null,
          'Editorial Range': selectedQuestion.publisherQualityIndicator,
          'keywords': collection.info
            ? collection.info.keywords
              ? collection.info.keywords[0]
              : null
            : null,
          'audience': collection.metadata
            ? collection.metadata.audience
              ? audience[collection.metadata.audience]
              : null
            : null,
        },

        media: {
          'format': collection.content_subformat
            ? collection.content_subformat.replace(/_/g, ' ')
            : null,
          'media Features': collection.media ? collection.media : null,
          'access hazard': collection.accesshazard
            ? collection.accesshazard
            : 'None',
          'advertisement level': collection.metadata
            ? collection.metadata.advertisement_level
              ? advertisementLevel[collection.metadata.advertisement_level]
              : 'Low'
            : 'Low',
          'framebreaker': collection.display_guide
            ? collection.display_guide.is_frame_breaker === 1
              ? 'Yes'
              : 'No'
            : 'No',
          'Is Broken': collection.publish_date
            ? collection.publish_date.is_broken
            : 'No',
          'address': collection.address ? collection.address : 'None',
        },

        instructional: {
          'Depth of Knowledge': collection.depthofknowledge.map((item: any) => item.label).toString(),
          '21st Century Skills': collection.skills,
        },

        framework: {
          subject: selectedQuestion.taxonomySubject,
          course: selectedQuestion.taxonomyCourse
            ? gutCourseNames(selectedQuestion.taxonomyCourse).join(', ')
            : null,
          domain: selectedQuestion.taxonomyDomain
            ? selectedQuestion.taxonomyDomain.join(', ')
            : null,
          standard: selectedQuestion.standards ? selectedQuestion.standards : [],
        },

        vector: {
          relevance: selectedQuestion.relevance || defaultVectorValue,
          engagement: selectedQuestion.engagement || defaultVectorValue,
          efficacy: selectedQuestion.efficacy || defaultVectorValue,
        },
      };
    }
    return resultSet;
  }

}

export const questionSerializer = QuestionSerializer.instance;
